import "@/App.css";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./components/ThemeProvider";
import { ProtectedRoute } from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { FloatingPomodoroTimer } from "./components/FloatingPomodoroTimer";
import { useColorThemeStore, useLanguageStore, useAuthStore, useFlashcardStore, usePremiumStore } from "./store";
import { useVersionCheck } from "./hooks/useVersionCheck";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AuthSuccess from "./pages/AuthSuccess";

// Lazy loaded components for code splitting
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const EssayModule = lazy(() => import("./pages/EssayModule"));
const MathModule = lazy(() => import("./pages/MathModule"));
const SubjectModule = lazy(() => import("./pages/SubjectModule"));
const UploadModule = lazy(() => import("./pages/UploadModule"));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard"));
const TeacherAnalytics = lazy(() => import("./pages/TeacherAnalytics"));
const ExamPage = lazy(() => import("./pages/ExamPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const FounderDashboard = lazy(() => import("./pages/FounderDashboard"));
const FlashcardsPage = lazy(() => import("./pages/FlashcardsPage"));
const AdminPortal = lazy(() => import("./pages/AdminPortal"));
const LanguageModule = lazy(() => import("./pages/LanguageModule"));
const MusicModule = lazy(() => import("./pages/MusicModule"));
const ChemistryModule = lazy(() => import("./pages/ChemistryModule"));
const LatinModule = lazy(() => import("./pages/LatinModule"));
const InformatikModule = lazy(() => import("./pages/InformatikModule"));
const SupportAgent = lazy(() => import("./pages/SupportAgent"));
const QuizModule = lazy(() => import("./pages/QuizModule"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const PresentationPage = lazy(() => import("./pages/PresentationPage"));
const DayPlannerPage = lazy(() => import("./pages/DayPlannerPage"));
const PomodoroTimerPage = lazy(() => import("./pages/PomodoroTimerPage"));
const FarbthemaPage = lazy(() => import("./pages/FarbthemaPage"));
const PremiumPage = lazy(() => import("./pages/PremiumPage"));

// Module-level init singleton — survives StrictMode double-mount
// Shared across component remounts so init only runs once
const _appInit = {
  promise: null,
  splashStart: Date.now(),
};

// Module-level ready flag — if AppRouter remounts (e.g. hot-reload, StrictMode),
// it initializes phase as 'ready' immediately so splash NEVER re-triggers
let _isAppReady = false;

// ─────────────────────────────────────────────
// Splash Screen (React-controlled, shown once)
// ─────────────────────────────────────────────
const SplashScreen = () => (
  <div style={{
    position: 'fixed', inset: 0,
    background: '#1F2D5A',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: '20px', zIndex: 9999,
  }}>
    <img
      src="/icons/icon-512.png"
      alt="Mentis"
      style={{ width: 100, height: 100, animation: 'splashPulse 1.8s ease-in-out infinite' }}
    />
    <span style={{
      color: '#ffffff', fontSize: '28px', fontWeight: 700,
      letterSpacing: '2px',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    }}>
      Mentis
    </span>
    <style>{`
      @keyframes splashPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.85; transform: scale(0.97); }
      }
    `}</style>
  </div>
);

// ─────────────────────────────────────────────
// Page loader (Suspense fallback only)
// ─────────────────────────────────────────────
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// ─────────────────────────────────────────────
// Main Router — handles ONE-TIME initialization
// ─────────────────────────────────────────────
const AppRouter = () => {
  const { initTheme } = useColorThemeStore();
  const { initLanguage } = useLanguageStore();
  const { checkAuth, isAuthenticated } = useAuthStore();
  const { syncPendingReviews } = useFlashcardStore();
  const { fetchStatus: fetchPremiumStatus } = usePremiumStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Automatically reload when a new deployment is detected.
  useVersionCheck();

  // Initialize from module-level flag — if AppRouter remounts after init,
  // skip splash entirely and go straight to 'ready'
  const [phase, setPhase] = useState(() => _isAppReady ? 'ready' : 'splash');

  // Sync pending offline reviews when connection is restored
  useEffect(() => {
    const handleOnline = () => {
      syncPendingReviews().then((synced) => {
        if (synced > 0) {
          console.log(`[App] Synced ${synced} pending reviews after reconnect`);
        }
      });
      // Refresh premium status on reconnect
      fetchPremiumStatus();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    // Already initialized (e.g. hot-reload, StrictMode remount) — no splash
    if (_isAppReady) {
      setPhase('ready');
      return;
    }

    // Remove the HTML splash immediately — React SplashScreen takes over
    if (typeof window.__removeSplash === 'function') {
      window.__removeSplash();
    }

    // Start init ONCE (module-level promise survives StrictMode double-mount)
    if (!_appInit.promise) {
      _appInit.promise = (async () => {
        initTheme();
        initLanguage();
        const authedUser = await checkAuth();
        // Minimum 900ms splash so "Mentis" text is visible
        const wait = Math.max(0, 900 - (Date.now() - _appInit.splashStart));
        if (wait > 0) await new Promise(r => setTimeout(r, wait));
        return { authedUser };
      })();
    }

    let active = true;
    _appInit.promise.then(({ authedUser }) => {
      if (!active) return;
      // Set module-level flag FIRST — prevents any remount from showing splash
      _isAppReady = true;
      // Fetch premium status after auth resolves
      if (authedUser) {
        fetchPremiumStatus();
      }
      // Navigate before showing app to avoid flash
      const path = window.location.pathname;
      if (authedUser) {
        if (path === '/' || path === '/login') {
          navigate('/dashboard', { replace: true });
        }
      } else {
        const isPublic = ['/', '/login', '/auth/success', '/invite', '/admin'].some(
          p => path === p || path.startsWith(p + '/')
        );
        if (!isPublic) navigate('/', { replace: true });
      }
      setPhase('ready');
    });

    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once — module-level flags prevent double-init and re-splash

  // Show splash screen during initialization
  if (phase === 'splash') {
    return <SplashScreen />;
  }

  // App is initialized — render routes
  return (
    <Suspense fallback={<PageLoader />}>
      <FloatingPomodoroTimer />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/success" element={<AuthSuccess />} />
        <Route path="/invite/:code" element={<InvitePage />} />

        {/* Protected Routes */}
        <Route path="/dashboard"        element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/referral"         element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
        <Route path="/review"           element={<ProtectedRoute><ReviewPage /></ProtectedRoute>} />
        <Route path="/support"          element={<ProtectedRoute><SupportAgent /></ProtectedRoute>} />
        <Route path="/quiz"             element={<ProtectedRoute><QuizModule /></ProtectedRoute>} />
        <Route path="/deutsch"          element={<ProtectedRoute><EssayModule subject="deutsch" /></ProtectedRoute>} />
        <Route path="/englisch"         element={<ProtectedRoute><LanguageModule language="englisch" /></ProtectedRoute>} />
        <Route path="/mathe"            element={<ProtectedRoute><MathModule /></ProtectedRoute>} />
        <Route path="/physik"           element={<ProtectedRoute><SubjectModule subject="physik" /></ProtectedRoute>} />
        <Route path="/chemie"           element={<ProtectedRoute><ChemistryModule /></ProtectedRoute>} />
        <Route path="/biologie"         element={<ProtectedRoute><SubjectModule subject="biologie" /></ProtectedRoute>} />
        <Route path="/geographie"       element={<ProtectedRoute><SubjectModule subject="geographie" /></ProtectedRoute>} />
        <Route path="/geschichte"       element={<ProtectedRoute><SubjectModule subject="geschichte" /></ProtectedRoute>} />
        <Route path="/musik"            element={<ProtectedRoute><MusicModule /></ProtectedRoute>} />
        <Route path="/latein"           element={<ProtectedRoute><LatinModule /></ProtectedRoute>} />
        <Route path="/informatik"       element={<ProtectedRoute><InformatikModule /></ProtectedRoute>} />
        <Route path="/spanisch"         element={<ProtectedRoute><LanguageModule language="spanisch" /></ProtectedRoute>} />
        <Route path="/italienisch"      element={<ProtectedRoute><LanguageModule language="italienisch" /></ProtectedRoute>} />
        <Route path="/franzoesisch"     element={<ProtectedRoute><LanguageModule language="franzoesisch" /></ProtectedRoute>} />
        <Route path="/upload"           element={<ProtectedRoute><UploadModule /></ProtectedRoute>} />
        <Route path="/flashcards"       element={<ProtectedRoute><FlashcardsPage /></ProtectedRoute>} />
        <Route path="/presentation"     element={<ProtectedRoute><PresentationPage /></ProtectedRoute>} />
        <Route path="/day-planner"      element={<ProtectedRoute><DayPlannerPage /></ProtectedRoute>} />
        <Route path="/pomodoro"         element={<ProtectedRoute><PomodoroTimerPage /></ProtectedRoute>} />
        <Route path="/farbthema"        element={<ProtectedRoute><FarbthemaPage /></ProtectedRoute>} />
        <Route path="/premium"          element={<ProtectedRoute><PremiumPage /></ProtectedRoute>} />
        <Route path="/teacher"          element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
        <Route path="/teacher/analytics" element={<ProtectedRoute><TeacherAnalytics /></ProtectedRoute>} />
        <Route path="/exams"            element={<ProtectedRoute><ExamPage /></ProtectedRoute>} />
        <Route path="/exams/:examId"    element={<ProtectedRoute><ExamPage /></ProtectedRoute>} />
        <Route path="/founder-dashboard" element={<ProtectedRoute><FounderDashboard /></ProtectedRoute>} />
        <Route path="/admin"            element={<AdminPortal />} />
        <Route path="/settings"         element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="*"                 element={<LandingPage />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
